/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { BaseService } from '../BaseService';
import { supabase, isDemoMode } from '../../lib/supabase';
import { User, UserRole } from '../../types';
import { logSecurityEvent } from '../../utils/securityUtils';
import { sha256Hex } from '../../utils/cryptoUtils';
import { isPlatformRole, isWorkspaceRole, normalizeAccountRole, resolveMvpAccountRole } from '../../lib/auth/accountRoles';
import { signInWithTrustedPasswordTelemetry } from '../trustedPasswordLogin';

// Helper to ensure supabase is available
function getSupabase() {
  if (isDemoMode) {
    throw new Error('UserService operations are not available in demo mode');
  }
  return supabase;
}

// Define Role interface
export interface Role {
  id: string;
  name: string;
  permissions: string[];
  description: string;
}

/**
 * UserService - Handles all user-related operations with improved error handling
 * and integration with Supabase
 */
class UserService extends BaseService<User> {
  constructor() {
    super('users'); // Table name in Supabase
  }

  // ---- Test compatibility wrappers (legacy API expected by unit tests) ----
  /**
   * Legacy: get user by ID and return raw record (no formatting)
   */
  async getUser(id: string): Promise<any> {
    const { data, error } = await getSupabase()
      .from(this.baseUrl)
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw new Error(error.message || 'User not found');
    return data;
  }

  /**
   * Legacy: create a user (basic fields) and log event
   */
  async createUser(user: Record<string, unknown>): Promise<void> {
    await getSupabase()
      .from(this.baseUrl)
      .insert({
        ...user,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    // The unit test only asserts that logSecurityEvent was called (no strict shape)
    // so we keep a simple, single-argument payload here for compatibility.
    try {
       
       
       
      (logSecurityEvent as any)({ action: 'user_created', details: { email: user?.['email'] } });
    } catch {}
  }

  /**
   * Legacy: update a user by ID with updatedAt timestamp
   */
  async updateUser(_id:  string, updates: Record<string, unknown>): Promise<void> {
    await getSupabase()
      .from(this.baseUrl)
      .update({
        ...updates,
        updatedAt: new Date().toISOString(),
      })
      .eq('id', _id);
  }

  /**
   * Suspend a user via secure RPC (replaces hard delete).
   * Direct deletion is blocked by RLS - must use RPC.
   * Actor is derived from auth.uid() on backend.
   * 
   * @param id - User ID to suspend
   * @param reason - Optional reason for suspension
   */
  async deleteUser(id: string, reason?: string): Promise<void> {
    const { data, error } = await getSupabase()
      .rpc('update_user_account_status_secure', {
        p_target_user_id: id,
        p_operation: 'suspend',
        p_reason: reason || 'Suspended by administrator',
      });
    
    if (error) {
      // Parse security policy errors for user-friendly display
      const message = error.message || 'Failed to suspend user';
      throw new Error(`Blocked by security policy: ${message}`);
    }
    
    if (!data?.success) throw new Error(data?.message || 'Account suspension was not applied');

    try {
       
       
       
      (logSecurityEvent as any)({ action: 'user_suspended', userId: id, details: { source: 'UserService', reason } });
    } catch {}
  }

  /**
   * Reactivate a suspended user via secure RPC.
   * 
   * @param id - User ID to reactivate
   */
  async reactivateUser(id: string): Promise<void> {
    const { data, error } = await getSupabase()
      .rpc('update_user_account_status_secure', {
        p_target_user_id: id,
        p_operation: 'reactivate',
        p_reason: null,
      });
    if (error) {
      const message = error.message || 'Failed to reactivate user';
      throw new Error(`Blocked by security policy: ${message}`);
    }

    if (!data?.success) throw new Error(data?.message || 'Account reactivation was not applied');
    
     
    try {
       
       
      (logSecurityEvent as any)({ action: 'user_reactivated', userId: id, details: { source: 'UserService' } });
    } catch {}
  }

  /**
   * Legacy: query helpers used in tests
   */
  async getUsersByRole(role:  string): Promise<void> {
    await getSupabase().from(this.baseUrl).select('*').eq('getUsersByRole(', role);
  }

  async getUsersByDepartment(department:  string): Promise<void> {
    await getSupabase().from(this.baseUrl).select('*').eq('department', department);
  }

  /**
   * @deprecated Use deleteUser() which calls the governed account lifecycle RPC.
   */
  async deactivateUser(id: string): Promise<void> {
    // Redirect to secure RPC
    await this.deleteUser(id, 'Deactivated by admin');
  }

  /**
   * @deprecated Use reactivateUser() which calls the governed account lifecycle RPC.
   */
  async activateUser(id: string): Promise<void> {
    // Redirect to secure RPC
    await this.reactivateUser(id);
  }

  async searchUsers(term:  string): Promise<void> {
    await getSupabase()
      .from(this.baseUrl)
      .select('*')
      .or(`name.ilike.%${term}%,email.ilike.%${term}%`);
  }

  /**
   * Get users with comprehensive filtering, sorting, and pagination
   */
  async getUsers(options:  {
    role?: UserRole;
    department?: string;
    status?: 'active' | 'inactive' | 'pending';
    search?: string;
    sortBy?: 'name' | 'role' | 'lastActive' | 'joinDate';
    sortDirection?: 'asc' | 'desc';
    limit?: number;
    page?: number;
  } = {}): Promise<{ data: User[]; count: number; error: Error | null }> {
    try {
      const {
        role,
        department,
        status,
        search,
        sortBy = 'lastActive',
        sortDirection = 'desc',
        limit = 50,
        page = 0
      } = options;

      let query = getSupabase().from(this.baseUrl).select('*', { count: 'exact' });

      // Apply filters
      if (role) query = query.eq('role', role);
      if (department) query = query.eq('department', department);
      if (status) query = query.eq('status', status);
      if (search) {
        query = query.or(
          `name.ilike.%${search}%,email.ilike.%${search}%,department.ilike.%${search}%`
        );
      }

      // Apply sorting
      const dbSortColumn = this.mapSortColumn(sortBy);
      query = query.order(dbSortColumn, { ascending: sortDirection === 'asc' });

      // Apply pagination
      if (limit) query = query.limit(limit);
      if (page > 0) query = query.range(page * limit, (page + 1) * limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      // Format user data
      const formattedUsers = this.formatUsers(data || []);

      return { data: formattedUsers, count: count || 0, error: null };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { data: [], count: 0, error: error as Error };
    }
  }

  /**
   * Get a user by ID with detailed error handling
   */
  async getUserById(_id:  string): Promise<{ data: User | null; error: Error | null }> {
    try {
      const { data, error } = await getSupabase()
        .from(this.baseUrl)
        .select('*')
        .eq('id', _id)
        .single();

      if (error) throw error;
      
      return { 
        data: data ? this.formatUser(data) : null, 
        error: null 
      };
    } catch (error) {
      console.error(`Error fetching user ${_id}:`, error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get a user's role details with permissions
   */
  async getUserRole(userId:  string): Promise<{ data: Role | null; error: Error | null }> {
    try {
      // First get the user to find their role
      const { data: user, error: userError } = await this.getUserById(userId);
      
      if (userError) throw userError;
      if (!user) return { data: null, error: new Error('User not found') };
      
      // Then get the role details
      const { data, error } = await getSupabase()
        .from('project_roles')
        .select('*')
        .eq('name', user.role)
        .single();
        
      if (error) throw error;
      
      return { 
        data: data ? {
          id: data.id,
          name: data.name,
          permissions: data.permissions || [],
          description: data.description || ''
        } : null, 
        error: null 
      };
    } catch (error) {
      console.error(`Error fetching role for user ${userId}:`, error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Update user profile with security logging and validation
   */
  async updateProfile(userId:  string, 
    profileData: Partial<User>,
    currentUserId: string
  ): Promise<{ data: User | null; error: Error | null }> {
    try {
      // Validate updates
      const allowedFields = ['name', 'department', 'company', 'phone', 'avatar', 'timezone', 'country'];
      const filteredUpdates = Object.keys(profileData).reduce((acc, key) => {
        if (allowedFields.includes(key)) {
          acc[key] = profileData[key as keyof Partial<User>];
        }
        return acc;
      }, {} as Record<string, any>);

      // Normalize string fields at the edge (trim whitespace, convert empty to null)
      if ('timezone' in filteredUpdates) {
        filteredUpdates.timezone = filteredUpdates.timezone?.trim() || null;
      }
      if ('country' in filteredUpdates) {
        filteredUpdates.country = filteredUpdates.country?.trim() || null;
      }
      if ('phone' in filteredUpdates) {
        filteredUpdates.phone = filteredUpdates.phone?.trim() || null;
      }

      // No allowed fields to update
      if (Object.keys(filteredUpdates).length === 0) {
        return { data: null, error: new Error('No valid fields to update') };
      }

      const { data, error } = await getSupabase()
        .from(this.baseUrl)
        .update(filteredUpdates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;

      // Log the profile update
      logSecurityEvent('permission_change', currentUserId, {
        targetUserId: userId,
        fields: Object.keys(filteredUpdates),
        operation: 'profile_update'
      }, 'success');

      return { data: this.formatUser(data), error: null };
    } catch (error) {
      // Log failed update
      logSecurityEvent('permission_change', currentUserId, {
        targetUserId: userId,
        error: (error as Error).message,
        operation: 'profile_update'
      }, 'failure');

      console.error(`Error updating profile for user ${userId}:`, error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Change user role with security logging
   */
  async changeRole(userId:  string, 
    role: UserRole,
    adminId: string
  ): Promise<{ data: User | null; error: Error | null }> {
    try {
      if (!isWorkspaceRole(role)) {
        throw new Error(`Blocked by security policy: unsupported role transition for "${role}"`);
      }

      const { error: roleError } = await getSupabase().rpc('update_workspace_role_secure', {
        p_target_user: userId,
        p_new_role: role,
      });
      if (roleError) {
        throw roleError;
      }

      const { data, error } = await getSupabase()
        .from(this.baseUrl)
        .select('*')
        .eq('id', userId)
        .single();
      if (error) throw error;

      // Log the role change with canonical account-role vocabulary in the payload.
      const canonicalAccountRole = resolveMvpAccountRole(role) ?? normalizeAccountRole(role);
      logSecurityEvent('role_change', adminId, {
        targetUserId: userId,
        newAccountRole: canonicalAccountRole ?? role,
        requestedRole: role,
      }, 'success');

      return { data: this.formatUser(data), error: null };
    } catch (error) {
      // Log failed role change
      const canonicalAccountRole = resolveMvpAccountRole(role) ?? normalizeAccountRole(role);
      logSecurityEvent('role_change', adminId, {
        targetUserId: userId,
        newAccountRole: canonicalAccountRole ?? role,
        requestedRole: role,
        error: (error as Error).message,
      }, 'failure');

      console.error(`Error changing role for user ${userId}:`, error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Activate or deactivate a user with security logging
   */
  async setUserStatus(userId:  string, 
    status: 'active' | 'inactive' | 'pending',
    adminId: string
  ): Promise<{ data: User | null; error: Error | null }> {
    try {
      if (status === 'pending') {
        throw new Error('Pending invitation state is managed by the invitation workflow.');
      }
      const operation = status === 'inactive' ? 'suspend' : 'reactivate';
      const { data: lifecycle, error } = await getSupabase()
        .rpc('update_user_account_status_secure', {
          p_target_user_id: userId,
          p_operation: operation,
          p_reason: operation === 'suspend' ? 'Status changed by administrator' : null,
        });

      if (error) throw error;
      if (!lifecycle?.success) throw new Error(lifecycle?.message || 'Account status was not changed');

      const { data, error: readError } = await getSupabase()
        .from(this.baseUrl)
        .select('*')
        .eq('id', userId)
        .single();
      if (readError) throw readError;

      // Lifecycle taxonomy: status mutations are not user_creation events.
      const lifecycleEvent =
        status === 'inactive'
          ? 'user_suspended'
          : status === 'active'
            ? 'user_reactivated'
            : 'membership_status_change';

      logSecurityEvent(lifecycleEvent, adminId, {
        targetUserId: userId,
        newStatus: status,
        operation: 'status_change',
      }, 'success');

      return { data: this.formatUser(data), error: null };
    } catch (error) {
      const lifecycleEvent =
        status === 'inactive'
          ? 'user_suspended'
          : status === 'active'
            ? 'user_reactivated'
            : 'membership_status_change';

      logSecurityEvent(lifecycleEvent, adminId, {
        targetUserId: userId,
        newStatus: status,
        error: (error as Error).message,
        operation: 'status_change',
      }, 'failure');

      console.error(`Error updating status for user ${userId}:`, error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get all available user roles
   */
  async getAllRoles(): Promise<{ data: Role[]; error: Error | null }> {
    try {
      const { data, error } = await getSupabase()
        .from('project_roles')
        .select('*');

      if (error) throw error;

      return { 
        data: (data || []).map(role => ({
          id: role.id,
          name: role.name,
          permissions: role.permissions || [],
          description: role.description || ''
        })), 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching roles:', error);
      return { data: [], error: error as Error };
    }
  }

  /**
   * Get users assigned to a project
   */
  async getProjectUsers(projectId:  string): Promise<{ data: User[]; error: Error | null }> {
    try {
      const { data, error } = await getSupabase()
        .from('project_users')
        .select('user_id')
        .eq('project_id', projectId);

      if (error) throw error;
      
      if (!data || data.length === 0) {
        return { data: [], error: null };
      }
      
      const userIds = data.map(item => item.user_id);
      
      const { data: users, error: usersError } = await getSupabase()
        .from(this.baseUrl)
        .select('*')
        .in('id', userIds);
        
      if (usersError) throw usersError;
      
      return { 
        data: this.formatUsers(users || []), 
        error: null 
      };
    } catch (error) {
      console.error(`Error fetching users for project ${projectId}:`, error);
      return { data: [], error: error as Error };
    }
  }

  /**
   * Invite a new user with security logging
   */
  async inviteUser(email:  string, 
    role: UserRole, 
    inviterId: string,
    options?: { projectId?: string; message?: string }
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      // Check if user already exists
      const { data: existingUser } = await getSupabase()
        .from(this.baseUrl)
        .select('id')
        .eq('email', email)
        .single();

      if (existingUser) {
        return { success: false, error: new Error('User with this email already exists') };
      }

      // In a real app, this would send an invitation email with a signup link
      // For demo purposes, we'll just create the user with a pending status
      const { error } = await getSupabase().from(this.baseUrl).insert({
        email,
        role,
        status: 'pending',
        created_at: new Date().toISOString(),
        last_active: new Date().toISOString(),
      });

      if (error) throw error;

      // If a project was specified, add the user to the project
      if (options?.projectId) {
        // Get the user ID of the newly created user
        const { data: newUser } = await getSupabase()
          .from(this.baseUrl)
          .select('id')
          .eq('email', email)
          .single();
          
        if (newUser) {
          await getSupabase().from('project_users').insert({
            project_id: options.projectId,
            user_id: newUser.id,
            role: role
          });
        }
      }

      // Log the invitation
      logSecurityEvent('user_creation', inviterId, {
        email,
        role,
        projectId: options?.projectId,
        operation: 'user_invite'
      }, 'success');

      return { success: true, error: null };
    } catch (error) {
      // Log failed invitation
      logSecurityEvent('user_creation', inviterId, {
        email,
        role,
        error: (error as Error).message,
        operation: 'user_invite'
      }, 'failure');

      console.error(`Error inviting user ${email}:`, error);
      return { success: false, error: error as Error };
    }
  }
  
  /**
   * Update a user's password
   */
  async updatePassword(userId:  string,
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const client = getSupabase();
      const { data: authUser, error: userError } = await client.auth.getUser();
      if (userError) throw userError;
      if (!authUser.user?.email) {
        throw new Error('Password authentication is not available for this account');
      }

      // Reauthenticate before changing a password. This prevents a stolen
      // browser session from changing credentials without knowing the current
      // password.
      const { error: reauthenticationError } = await signInWithTrustedPasswordTelemetry(
        client,
        authUser.user.email,
        currentPassword,
      );
      if (reauthenticationError) {
        throw new Error('Current password is incorrect');
      }

      const { error } = await client.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      // Log the password change
      logSecurityEvent('password_change', userId, {}, 'success');

      return { success: true, error: null };
    } catch (error) {
      // Log failed password change
      logSecurityEvent('password_change', userId, {
        error: (error as Error).message,
      }, 'failure');

      console.error(`Error updating password for user ${userId}:`, error);
      return { success: false, error: error as Error };
    }
  }

  /**
   * Revoke every refresh-token session except the current browser session.
   */
  async signOutOtherSessions(
    userId: string,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await getSupabase().auth.signOut({ scope: 'others' });
      if (error) throw error;
      logSecurityEvent('logout', userId, {
        operation: 'sign_out_other_sessions',
      }, 'success');
      return { success: true, error: null };
    } catch (error) {
      logSecurityEvent('logout', userId, {
        operation: 'sign_out_other_sessions',
        error: (error as Error).message,
      }, 'failure');
      return { success: false, error: error as Error };
    }
  }
  
  /**
   * Enable or disable two-factor authentication for a user
   */
  async toggleTwoFactorAuth(userId:  string, 
    enable: boolean
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await getSupabase()
        .from(this.baseUrl)
        .update({ two_factor_enabled: enable })
        .eq('id', userId);

      if (error) throw error;

      // Log the 2FA change
      logSecurityEvent('user_creation', userId, {
        action: enable ? 'enabled' : 'disabled',
        operation: 'two_factor_auth'
      }, 'success');

      return { success: true, error: null };
    } catch (error) {
      // Log failed 2FA change
      logSecurityEvent('user_creation', userId, {
        action: enable ? 'enabled' : 'disabled',
        error: (error as Error).message,
        operation: 'two_factor_auth'
      }, 'failure');

      console.error(`Error updating 2FA for user ${userId}:`, error);
      return { success: false, error: error as Error };
    }
  }

  /**
   * Start TOTP enrollment - returns QR code and secret
   */
  async enrollMfaTotp(userId: string): Promise<{
    success: boolean;
    factorId?: string;
    qrCode?: string;
    secret?: string;
    error?: Error;
  }> {
    try {
      const supabase = getSupabase();
      
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'AX1 Capital Authenticator'
      });

      if (error) throw error;

      logSecurityEvent('mfa', userId, {
        method: 'mfa.enroll.start',
        factorId: data.id
      }, 'success');

      return {
        success: true,
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret
      };

    } catch (error) {
      logSecurityEvent('mfa', userId, {
        method: 'mfa.enroll.start',
        error: (error as Error).message
      }, 'failure');

      console.error(`Error starting MFA enrollment for user ${userId}:`, error);
      return { success: false, error: error as Error };
    }
  }

  /**
   * Verify and complete TOTP enrollment
   */
  async verifyMfaEnrollment(
    userId: string,
    factorId: string,
    code: string
  ): Promise<{ success: boolean; error?: Error }> {
    try {
      const supabase = getSupabase();
      
      // Create challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId
      });

      if (challengeError) throw challengeError;

      // Verify with code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code
      });

      if (verifyError) throw verifyError;

      const { data: assurance, error: assuranceError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (assuranceError) throw assuranceError;
      if (assurance?.currentLevel !== 'aal2') {
        throw new Error('Supabase did not confirm an AAL2 session after enrollment verification');
      }

      // Update profile to mark 2FA as enabled
      const { error: updateError } = await supabase
        .from(this.baseUrl)
        .update({ 
          two_factor_enabled: true,
          two_factor_enrolled_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) {
        console.warn('Failed to update profile 2FA status:', updateError);
      }

      logSecurityEvent('mfa', userId, {
        method: 'mfa.enroll.success',
        factorId
      }, 'success');

      return { success: true };

    } catch (error) {
      logSecurityEvent('mfa', userId, {
        method: 'mfa.enroll.success',
        error: (error as Error).message
      }, 'failure');

      console.error(`Error verifying MFA enrollment for user ${userId}:`, error);
      return { success: false, error: error as Error };
    }
  }

  /** Remove only an unfinished enrollment factor when the setup dialog closes. */
  async cancelMfaEnrollment(userId: string, factorId: string): Promise<void> {
    try {
      const { error } = await getSupabase().auth.mfa.unenroll({ factorId });
      if (error) throw error;
      logSecurityEvent('mfa', userId, {
        method: 'mfa.enroll.cancel',
        factorId,
      }, 'success');
    } catch (error) {
      logSecurityEvent('mfa', userId, {
        method: 'mfa.enroll.cancel',
        factorId,
        error: (error as Error).message,
      }, 'failure');
      console.warn(`Failed to remove unfinished MFA factor ${factorId}:`, error);
    }
  }

  /**
   * Unenroll (disable) MFA for a user
   * Removes ALL TOTP factors and invalidates recovery codes
   */
  async unenrollMfa(
    userId: string,
    factorId: string
  ): Promise<{ success: boolean; error?: Error }> {
    try {
      const supabase = getSupabase();
      
      // Get all factors to ensure we remove all of them
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const allFactors = factorsData?.totp || [];
      
      // Unenroll all TOTP factors (not just the provided one)
      const factorIds = allFactors.map(f => f.id);
      if (!factorIds.includes(factorId)) {
        factorIds.push(factorId);
      }
      
      for (const fid of factorIds) {
        try {
          await supabase.auth.mfa.unenroll({ factorId: fid });
        } catch (e) {
          // Factor may already be removed, continue
          console.warn(`Failed to unenroll factor ${fid}:`, e);
        }
      }

      // Delete all recovery codes for this user
      const { error: deleteCodesError } = await supabase
        .from('user_recovery_codes')
        .delete()
        .eq('user_id', userId);
      
      if (deleteCodesError) {
        console.warn('Failed to delete recovery codes:', deleteCodesError);
      }

      // Update profile to mark 2FA as disabled
      const { error: updateError } = await supabase
        .from(this.baseUrl)
        .update({ 
          two_factor_enabled: false,
          two_factor_enrolled_at: null
        })
        .eq('id', userId);

      if (updateError) {
        console.warn('Failed to update profile 2FA status:', updateError);
      }

      logSecurityEvent('mfa', userId, {
        method: 'mfa.disable.success',
        factorIds,
        recoveryCodesDeleted: true
      }, 'success');

      return { success: true };

    } catch (error) {
      logSecurityEvent('mfa', userId, {
        method: 'mfa.disable.success',
        error: (error as Error).message
      }, 'failure');

      console.error(`Error unenrolling MFA for user ${userId}:`, error);
      return { success: false, error: error as Error };
    }
  }

  /**
   * Get current MFA factors for a user
   */
  async getMfaFactors(): Promise<{
    factors: Array<{
      id: string;
      type: string;
      status: string;
      friendlyName?: string;
    }>;
    error?: Error;
  }> {
    try {
      const supabase = getSupabase();
      
      const { data, error } = await supabase.auth.mfa.listFactors();

      if (error) throw error;

      const factors = (data.totp || []).map(f => ({
        id: f.id,
        type: 'totp',
        status: f.status,
        friendlyName: f.friendly_name
      }));

      return { factors };

    } catch (error) {
      console.error('Error listing MFA factors:', error);
      return { factors: [], error: error as Error };
    }
  }

  /**
   * Generate recovery codes for a user
   * Returns plain codes - caller should display once then store hashes
   */
  async generateRecoveryCodes(
    userId: string,
    count: number = 10
  ): Promise<{ codes?: string[]; error?: Error }> {
    try {
      const supabase = getSupabase();
      
      // Generate random recovery codes
      const codes: string[] = [];
      for (let i = 0; i < count; i++) {
        const code = this.generateRecoveryCode();
        codes.push(code);
      }

      // Hash codes using SHA-256 (industry standard) and store in database
      const hashedCodes = await Promise.all(
        codes.map(async code => ({
          user_id: userId,
          code_hash: await this.hashRecoveryCodeSHA256(code),
          used: false,
          created_at: new Date().toISOString()
        }))
      );

      // SAFE ORDERING: Insert new codes first, then delete old ones
      // This ensures user never has zero codes if insert fails
      const insertTimestamp = new Date().toISOString();

      // Step 1: Insert new codes (with current timestamp)
      const { error } = await supabase
        .from('user_recovery_codes')
        .insert(hashedCodes);

      if (error) {
        // Insert failed - user still has their old codes (safe failure)
        throw new Error(`Failed to insert recovery codes: ${error.message}`);
      }

      // Step 2: Delete old codes (created before this operation)
      // This only runs if insert succeeded
      const { error: deleteError } = await supabase
        .from('user_recovery_codes')
        .delete()
        .eq('user_id', userId)
        .lt('created_at', insertTimestamp);

      if (deleteError) {
        // Delete failed but new codes exist - log warning but don't fail
        console.warn('Failed to delete old recovery codes:', deleteError);
      }

      logSecurityEvent('mfa', userId, {
        method: 'mfa.recovery.generated',
        count
      }, 'success');

      return { codes };

    } catch (error) {
      logSecurityEvent('mfa', userId, {
        method: 'mfa.recovery.generated',
        error: (error as Error).message
      }, 'failure');

      console.error(`Error generating recovery codes for user ${userId}:`, error);
      return { error: error as Error };
    }
  }

  /**
   * Consume a recovery code during MFA bypass
   */
  async consumeRecoveryCode(
    userId: string,
    code: string
  ): Promise<{ success: boolean; error?: Error }> {
    try {
      const supabase = getSupabase();
      
      const codeHash = await this.hashRecoveryCodeSHA256(code);

      // Server-side atomic transition prevents replay and client-side
      // rewriting of recovery-code rows.
      const { data, error } = await supabase.rpc('consume_recovery_code', {
        p_code_hash: codeHash,
      });

      if (error || data !== true) {
        logSecurityEvent('login', userId, {
          method: 'mfa.recovery.invalid'
        }, 'failure');
        return { success: false, error: new Error('Invalid or already used recovery code') };
      }

      logSecurityEvent('login', userId, {
        method: 'mfa.recovery.used'
      }, 'success');

      return { success: true };

    } catch (error) {
      console.error(`Error consuming recovery code for user ${userId}:`, error);
      return { success: false, error: error as Error };
    }
  }

  /**
   * Cleanup stale recovery codes after successful MFA verification
   * Removes old/duplicate codes from previous generation batches
   * Called silently after successful login - doesn't block auth flow
   * 
   * Logic:
   * - Find MAX(created_at) of unused codes = latest batch timestamp
   * - Delete all codes where created_at < (latestBatch - 1 minute)
   * - The 1-minute buffer ensures codes from the same batch (which may have
   *   slight timestamp variations) are preserved
   */
  async cleanupStaleRecoveryCodes(userId: string): Promise<void> {
    try {
      const supabase = getSupabase();

      // Find the most recent generation timestamp for this user's unused codes
      const { data: latestCode } = await supabase
        .from('user_recovery_codes')
        .select('created_at')
        .eq('user_id', userId)
        .eq('used', false)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!latestCode?.created_at) {
        // No unused codes found, nothing to clean
        return;
      }

      // Delete codes older than the latest batch (created more than 1 minute before the latest)
      // This gives a buffer for codes generated in the same batch
      const latestBatchTime = new Date(latestCode.created_at);
      const cutoffTime = new Date(latestBatchTime.getTime() - 60000); // 1 minute buffer

      const { data: deletedCodes, error } = await supabase
        .from('user_recovery_codes')
        .delete()
        .eq('user_id', userId)
        .lt('created_at', cutoffTime.toISOString())
        .select('id');

      if (error) {
        // Log but don't throw - this is a silent cleanup
        console.warn('Recovery code cleanup failed:', error.message);
        return;
      }

      // Log only if we cleaned up an unusual amount (potential issue indicator)
      const cleanedCount = deletedCodes?.length || 0;
      if (cleanedCount > 10) {
        console.warn(`Cleaned up ${cleanedCount} stale recovery codes for user ${userId} - unusually high count`);
      }

    } catch (error) {
      // Silent failure - don't disrupt the auth flow
      console.warn('Recovery code cleanup error:', error);
    }
  }

  /**
   * Generate a single recovery code in format XXXX-XXXX-XXXX
   */
  private generateRecoveryCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoiding ambiguous chars
    let code = '';
    for (let i = 0; i < 12; i++) {
      if (i > 0 && i % 4 === 0) code += '-';
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  /**
   * Hash a recovery code using SHA-256 (industry standard)
   * Normalizes input by removing dashes and uppercasing
   */
  private async hashRecoveryCodeSHA256(code: string): Promise<string> {
    // Remove dashes and normalize
    const normalized = code.replace(/-/g, '').toUpperCase();
    // Use SHA-256 via Web Crypto API
    return await sha256Hex(normalized);
  }

  /**
   * Regenerate recovery codes with reauth verification
   * Requires password + TOTP verification before regenerating
   */
  async regenerateRecoveryCodes(
    userId: string,
    password: string,
    totpCode: string
  ): Promise<{ codes?: string[]; error?: Error }> {
    try {
      const supabase = getSupabase();
      
      // 1. Verify password by re-authenticating
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.email) {
        throw new Error('Unable to get user email for reauthentication');
      }

      // Attempt to sign in with current password to verify
      const { error: authError } = await signInWithTrustedPasswordTelemetry(
        supabase,
        userData.user.email,
        password,
      );

      if (authError) {
        logSecurityEvent('mfa', userId, {
          method: 'mfa.recovery.regenerate',
          step: 'password_reauth',
          error: 'Invalid password'
        }, 'failure');
        throw new Error('Invalid password');
      }

      // 2. Verify TOTP code
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const verifiedFactor = (factorsData?.totp || []).find(f => f.status === 'verified');

      if (!verifiedFactor) {
        throw new Error('No verified MFA factor found');
      }

      // Create challenge and verify
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: verifiedFactor.id
      });

      if (challengeError) {
        throw new Error('Failed to create MFA challenge');
      }

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: verifiedFactor.id,
        challengeId: challengeData.id,
        code: totpCode
      });

      if (verifyError) {
        logSecurityEvent('mfa', userId, {
          method: 'mfa.recovery.regenerate',
          step: 'totp_verify',
          error: 'Invalid TOTP code'
        }, 'failure');
        throw new Error('Invalid verification code');
      }

      // 3. Generate new recovery codes (reuse existing method logic)
      const codes: string[] = [];
      for (let i = 0; i < 10; i++) {
        const code = this.generateRecoveryCode();
        codes.push(code);
      }

      // Hash codes and store
      const hashedCodes = await Promise.all(
        codes.map(async code => ({
          user_id: userId,
          code_hash: await this.hashRecoveryCodeSHA256(code),
          used: false,
          created_at: new Date().toISOString()
        }))
      );

      // SAFE ORDERING: Insert new codes first, then delete old ones
      // This ensures user never has zero codes if insert fails
      const insertTimestamp = new Date().toISOString();

      // Step 1: Insert new codes (with current timestamp)
      const { error: insertError } = await supabase
        .from('user_recovery_codes')
        .insert(hashedCodes);

      if (insertError) {
        // Insert failed - user still has their old codes (safe failure)
        throw new Error(`Failed to insert new recovery codes: ${insertError.message}`);
      }

      // Step 2: Delete old codes (created before this operation)
      // This only runs if insert succeeded
      const { error: deleteError } = await supabase
        .from('user_recovery_codes')
        .delete()
        .eq('user_id', userId)
        .lt('created_at', insertTimestamp);

      if (deleteError) {
        // Delete failed but new codes exist - log warning but don't fail
        // User now has both old and new codes (slightly messy but safe)
        console.warn('Failed to delete old recovery codes:', deleteError);
        // We don't throw here - user has valid new codes
      }

      logSecurityEvent('mfa', userId, {
        method: 'mfa.recovery.regenerated',
        count: codes.length
      }, 'success');

      return { codes };

    } catch (error) {
      logSecurityEvent('mfa', userId, {
        method: 'mfa.recovery.regenerated',
        error: (error as Error).message
      }, 'failure');

      console.error(`Error regenerating recovery codes for user ${userId}:`, error);
      return { error: error as Error };
    }
  }
  
  /**
   * Map frontend sort column to database column
   */
  private mapSortColumn(column:  string): string {
    const columnMap: Record<string, string> = {
      name: 'name',
      role: 'role',
      lastActive: 'last_active',
      joinDate: 'created_at',
    };

    return columnMap[column] || 'last_active';
  }

  /**
   * Format multiple users
   */
  private formatUsers(users:  Record<string, unknown>[]): User[] {
    return users.map(user => this.formatUser(user));
  }

  /**
   * Format a single user
   */
  private formatUser(user:  Record<string, unknown>): User {
    // Fail closed: unknown/legacy values normalize to null (never defaulted to
    // a scoped relationship such as supplier token). The legacy assignment field is
    // display-only; canonical authority is carried by `account_role`.
    const accountRole = normalizeAccountRole(
      String(user.platform_role || user.workspace_role || user.role || ''),
    );
    return {
      id: String(user.id || ''),
      name: String(user.name || ''),
      email: String(user.email || ''),
      role: accountRole ?? 'member',
      account_role: resolveMvpAccountRole(String(user.account_role ?? '')),
      platform_role: accountRole && isPlatformRole(accountRole) ? accountRole : null,
      workspace_role: accountRole && isWorkspaceRole(accountRole) ? accountRole : undefined,
      avatar: user.avatar_url ? String(user.avatar_url) : '',
      department: String(user.department || ''),
      company: String(user.company || ''),
      phone: String(user.phone || ''),
      joinDate: new Date(String(user.created_at || '')),
      lastActive: new Date(String(user.last_active || new Date().toISOString())),
      status: (user.status as 'active' | 'inactive' | 'pending' | 'suspended') || 'active',
    };
  }

}

// Create a singleton instance
export const userService = new UserService();
export type { User, UserRole };



